import { CommonModule, KeyValuePipe, NgForOf, NgIf } from '@angular/common';
import { NgModule } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AuthenticationModule } from '@app/authentication';
import { FormFieldsModule } from '@app/base-components';
import { CartCustomModule, HabaCartSharedModule } from '@app/cart';
import { PaymentModeService, PlaceOrderService, ProceedService } from '@app/services';
import { CmsConfig, I18nModule, provideConfig, UrlModule } from 'haba-core';
import { CartNotEmptyGuard, CheckoutGuard } from 'haba-featurelibs/checkout/components';
import { CardModule, LayoutConfig } from 'haba-storefrontlib';
import { UserModule } from 'src/app/user/user.module';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';

import { EMPORIX_ORDER_NORMALIZER, EmporixOrderNormalizer } from 'haba-emporix';
import { DynamicGridModule } from '../contentful/dynamic-grid/dynamic-grid.module';
import { AddressBookComponent } from './address-book';
import { AddressMelissaCheckModalComponent } from './address-melissa-check-modal/address-melissa-check-modal.component';
import { AuthorizePaymentComponent } from './authorize-payment/authorize-payment.component';
import { BirthdateFormComponent } from './birthdate-form/birthdate-form.component';
import { CheckoutHeadlineComponent } from './checkout-headline';
import { CheckoutLoginHeadlineComponent, HabaCheckoutLoginComponent } from './checkout-login';
import { CheckoutLoginBottomComponent } from './checkout-login-bottom/checkout-login-bottom.component';
import { CheckoutLoginRegisterFormComponent } from './checkout-login-register-form/checkout-login-register-form.component';
import { CheckoutOrderSummaryComponent } from './checkout-order-summary';
import { CheckoutProductsComponent } from './checkout-products';
import { CheckoutProgressComponent } from './checkout-progress';
import { CheckoutStepsConfig } from './checkout-steps.config';
import { EditDataModalComponent } from './edit-data-modal/edit-data-modal.component';
import { HabaCheckoutAuthGuard } from './guards';
import { HabaCartValidationGuard } from './guards/haba-cart-validation-guard';
import { habaCheckoutBillingAddressGuard } from './guards/haba-checkout-billing-address.guard';
import { HabaCheckoutStepsSetGuard } from './guards/haba-checkout-steps-set.guard';
import { HabaCheckoutGuard } from './guards/haba-checkout.guard';
import { HabaExpressCheckoutGuard } from './guards/haba-express-checkout.guard';
import { HabaOrderConfirmationGuard } from './guards/haba-order-confirmation-guard';
import { HabaPaymentValidationGuard } from './guards/haba-payment-validation-guard';
import { AddressStepComponent } from './new-checkout/address';
import { OrderConfirmationThankYouMessageComponent } from './order-confirmation-thank-you-message';
import { PaymentModeComponent } from './payment-mode';
import { HabaPlaceOrderComponent } from './place-order';
import { HabaReviewSubmitComponent } from './review-submit';

@NgModule({
  imports: [
    NgbAccordionModule,
    CommonModule,
    ReactiveFormsModule,
    I18nModule,
    FormFieldsModule,
    HabaCartSharedModule,
    UrlModule,
    RouterModule,
    CartCustomModule,
    UserModule,
    AuthenticationModule,
    CardModule,
    DynamicGridModule,
    FormsModule,
    KeyValuePipe,
    NgForOf,
    NgIf,
    CheckoutOrderSummaryComponent,
    OrderConfirmationThankYouMessageComponent,
    PaymentModeComponent,
    HabaPlaceOrderComponent,
    HabaReviewSubmitComponent,
    HabaCheckoutLoginComponent,
    CheckoutProgressComponent,
    CheckoutProductsComponent,
    AddressBookComponent,
    CheckoutLoginHeadlineComponent,
    CheckoutHeadlineComponent,
    AddressMelissaCheckModalComponent,
    AuthorizePaymentComponent,
    CheckoutLoginBottomComponent,
    CheckoutLoginRegisterFormComponent,
    EditDataModalComponent,
    BirthdateFormComponent,
  ],
  exports: [CheckoutProgressComponent],
  providers: [
    PaymentModeService,
    PlaceOrderService,
    ProceedService,
    {
      provide: CheckoutGuard,
      useClass: HabaCheckoutGuard,
    },
    provideConfig(<CmsConfig>{
      cmsComponents: {
        AddressBookComponent: {
          component: AddressBookComponent,
        },
        BillingAddressComponent: {
          component: AddressStepComponent,
          guards: [
            // HabaCheckoutAuthGuard, //remove this guard to not block guest checkout
            CartNotEmptyGuard,
            habaCheckoutBillingAddressGuard,
            HabaCartValidationGuard,
            HabaCheckoutStepsSetGuard,
          ],
        },
        OrderConfirmationThankMessageComponent: {
          component: OrderConfirmationThankYouMessageComponent,
          guards: [HabaOrderConfirmationGuard],
        },
        CheckoutPaymentDetails: {
          component: PaymentModeComponent,
          guards: [
            HabaCheckoutAuthGuard,
            CartNotEmptyGuard,
            HabaCartValidationGuard,
            HabaCheckoutStepsSetGuard,
          ],
        },
        CheckoutOrderSummary: {
          component: CheckoutOrderSummaryComponent,
        },
        CheckoutReviewOrder: {
          component: HabaReviewSubmitComponent,
          guards: [
            HabaCheckoutAuthGuard,
            HabaPaymentValidationGuard,
            HabaExpressCheckoutGuard,
            CartNotEmptyGuard,
            HabaCartValidationGuard,
            HabaCheckoutStepsSetGuard,
          ],
          disableSSR: true,
        },
        CheckoutReviewOrderProductsComponent: {
          component: CheckoutProductsComponent,
          guards: [HabaCheckoutAuthGuard, CartNotEmptyGuard, HabaCartValidationGuard],
        },
        ReturningCustomerCheckoutLoginComponent: {
          component: HabaCheckoutLoginComponent,
        },
        B2BLoginCheckoutBottomComponent: {
          component: CheckoutLoginBottomComponent,
        },
        PromotionsComponent: {},
        PotentialPromotionsComponent: {},
        CheckoutProgress: {
          component: CheckoutProgressComponent,
          guards: [CartNotEmptyGuard, HabaCartValidationGuard],
        },
        CheckoutLoginRegistrationComponent: {
          component: CheckoutLoginRegisterFormComponent,
        },
        CheckoutLoginHeadlineComponent: {
          component: CheckoutLoginHeadlineComponent,
        },
        AuthorizePaymentComponent: {
          component: AuthorizePaymentComponent,
          disableSSR: true,
        },
      },
      ...CheckoutStepsConfig,
    }),
    provideConfig({
      routing: {
        routes: {
          checkoutBillingAddress: {
            paths: ['checkout/billing-address'],
          },
          authorizePayment: {
            paths: ['checkout/authorize-payment'],
          },
          orderConfirmation: {
            paths: ['order-confirmation'],
          },
          orderStatus: {
            paths: ['bestellstatus'],
          },
        },
      },
    }),
    provideConfig(<LayoutConfig>{
      layoutSlots: {
        MultiStepCheckoutSummaryPageTemplate: {
          lg: {
            slots: ['TopContent', 'HeadlineContent', 'BodyContent', 'SideContent'],
          },
          slots: ['TopContent', 'HeadlineContent', 'BodyContent', 'SideContent'],
        },
        MultiStepCheckoutPageTemplate: {
          slots: ['TopContent', 'HeadlineContent', 'LeftContent', 'RightContent', 'BottomContent'],
          lg: {
            slots: [
              'TopContent',
              'HeadlineContent',
              'LeftContent',
              'RightContent',
              'BottomContent',
            ],
          },
        },
        CheckoutLoginPageTemplate: {
          slots: [
            'TopHeaderSlot',
            'BottomHeaderSlot',
            'LeftContentSlot',
            'CenterContentSlot',
            'RightContentSlot',
            'BottomContent',
          ],
        },
      },
      // Disable the spinner at last step of checkout as long as we don't have styles for it
      launch: {
        PLACE_ORDER_SPINNER: undefined,
      },
    }),
    {
      provide: EMPORIX_ORDER_NORMALIZER,
      useExisting: EmporixOrderNormalizer,
      multi: true,
    },
  ],
})
export class CustomCheckoutModule {}
